import { sendCustomTrackingInfo } from '@panel/commons';

export const DL_STEP_TITLE = {
  1: 'Paso 1 - Propiedad y operación',
  2: 'Paso 2 - Ubicación',
  3: 'Paso 3 - Multimedia',
  4: 'Paso 4 - Información de la propiedad',
  5: 'Paso 5 - Adicionales',
  6: 'Paso 6 - Título y descripción',
  7: 'Paso 7 - Precio',
  8: {
    hasPlan: 'Paso 8 - Planes disponibles',
    noPlan: 'Paso 8 - Sin planes disponibles'
  }
};

export const OPERATION_TYPE = {
  1: 'Venta',
  2: 'Alquiler',
  4: 'Temporal'
  // 4: 'Emprendimiento'
};

/**
 * @param {string | null} postingId - ID del aviso, si existe.
 * @param {boolean} isEdition - Indica si el aviso está en modo edición.
 * @returns {'Nueva publicación' | 'Edición' | 'Borrador'}
 */
export const adaptPublicationStatus = (isEdition, isDraftFromPanel) => {
  if (isEdition) return 'Edición';
  if (isDraftFromPanel) return 'Borrador';
  return 'Nueva publicación';
};

export const buildOperationString = operations => {
  const labels = operations?.map(id => OPERATION_TYPE[id]).filter(Boolean);
  return labels?.join('|');
};

/**
 * La función retorna un string con el título del paso actual para
 * armar el indice step del evento que se envia.
 * @param {string | number} step
 * @param {boolean} isPlanEmpty
 * @returns
 */

export const setStepTitle = (step = '', isPlanEmpty = false) => {
  const isStepEight = step === 8;
  const stepTitle =
    isStepEight && typeof DL_STEP_TITLE[8] === 'object'
      ? !isPlanEmpty
        ? DL_STEP_TITLE[8].hasPlan
        : DL_STEP_TITLE[8].noPlan
      : DL_STEP_TITLE[step];
  return stepTitle;
};

/**
 * Obtiene el nombre del tipo o subtipo de propiedad seleccionado a partir de su ID.
 * @returns {string} - Nombre del tipo o del subtipo de propiedad seleccionado. Ej. Departamento, Casa, Casa de playa, etc.
 */
export const propertyTypeNameSelected = (selectedType, mainOptions = [], moreOptions = []) => {
  const allPropertyTypeOptions = [...mainOptions, ...moreOptions];
  const propertyType = allPropertyTypeOptions.find(
    type => type.realEstateTypeId === selectedType || type.realEstateSubTypeId === selectedType
  );
  return propertyType ? propertyType.name : '';
};

/**
 * Envía un evento de tracking para acciones en modales del publicador.
 * @param {string} eventName - Nombre del evento.
 * @param {string} modalName - Nombre del modal. Ej: "Modal guardar y salir", "Modal volver", "Modal salir"
 * @param {string} itemId - Acción realizada. Ej: "Salir", "Volver", "Guardar y salir", "Continuar editando"
 */
export const sendModalTrackingInfo = (eventName = '', modalName = '', itemId = '') => {
  if (!modalName) return;

  const payload = {
    event: eventName,
    content_group: 'Publicador',
    modal_name: modalName,
    ...(itemId && { item_id: itemId })
  };

  sendCustomTrackingInfo(payload);
};

/**
 * Envía un evento de tracking para acciones en la pantala de edicón del publicador.
 *
 * @param {object} postingData - Datos del aviso.
 * @param {string} itemId - Acción realizada. Ej: Republicar|Ver aviso|Ir a mis avisos
 * @param {string | number} postingId - ID del aviso.
 */
export const sendEditionTrackingInfo = (postingData = {}, itemId = '', postingId = '') => {
  if (!postingData) return;

  sendCustomTrackingInfo({
    event: 'edit_publication',
    content_group: 'Publicador',
    user_type: 'Profesional',
    step: 'Edición de aviso',
    item_id: itemId,
    operation_type: buildOperationString(postingData?.operationTypeIds),
    tipoDePropiedad: postingData?.realEstateTypeName,
    plan: postingData?.publication?.publicationPlan,
    subTipoDePropiedad: postingData?.realEstateSubTypeName || '',
    status: 'Edición',
    posting_id: postingId
  });
};

export const sendSuccessfulPublicationTrackingInfo = (
  postingId = '',
  postingData = {},
  isDraftFromPanel = false,
  isEdition = false
) => {
  if (!postingId || !postingData) return;

  sendCustomTrackingInfo({
    event: 'create_publication',
    content_group: 'Publicador',
    user_type: 'Profesional',
    step: 'Publicación exitosa',
    item_id: 'Continuar',
    operation_type: buildOperationString(postingData?.operationTypeIds),
    tipoDePropiedad: postingData?.realEstateTypeName,
    plan: postingData?.publication?.publicationPlan,
    subTipoDePropiedad: postingData?.realEstateSubTypeName || '',
    status: adaptPublicationStatus(isEdition, isDraftFromPanel),
    posting_id: postingId
  });
};

/**
 * Adapta los parametros para armar el trackingPayload y enviar la información de tracking DLs.
 * @param {string | number} step
 * @param {string | number} postingId
 * @param {string} item_id {Continuar|Guardar y salir|Volver|Guardar|Cancelar|Impresion modal sin planes}
 * @param {string} propertyTypeName
 * @param {string | number} operations_type {Venta|Alquiler|Temporal|Emprendimiento}
 * @param {boolean} isEdition
 * @param {string} selectedPlanName
 * @param {boolean} isDraftFromPanel Este parametro necesitamos enviarlo por url desde el panel cuando queremos terminar de publicar un borrador.
 * @param {string} propertySubTypeName
 * @returns Se envia la notificación con el evento.
 */
const adaptStepDataAndSendTrackinInfo = (
  step,
  postingId = null,
  item_id = '',
  propertyTypeName = '',
  operations_type = [],
  isEdition = false,
  selectedPlanName = '',
  isDraftFromPanel = false,
  isPlanEmpty = false,
  propertySubTypeName = ''
) => {
  const isPlansStep = step === 8;

  const trackingPayload = {
    event: 'create_publication',
    content_group: 'Publicador',
    user_type: 'Profesional',
    step: setStepTitle(step, isPlanEmpty),
    item_id: item_id,
    operation_type: buildOperationString(operations_type),
    tipoDePropiedad: propertyTypeName,
    subTipoDePropiedad: propertySubTypeName,
    status: adaptPublicationStatus(isEdition, isDraftFromPanel),
    posting_id: postingId || 'borrador generado'
  };

  if (isPlansStep) {
    trackingPayload.plan = selectedPlanName;
  }

  return sendCustomTrackingInfo(trackingPayload);
};

export default adaptStepDataAndSendTrackinInfo;

/**
 * Envía un evento de tracking para generar/deshacer texto IA en el paso de título y descripción.
 * @param {'Título'|'Descripción'} contentType
 * @param {'Generar'|'Deshacer'} itemId
 * @param {Array} operationsTypeIds
 * @param {string} propertyTypeName
 * @param {string} propertySubTypeName
 * @param {boolean} isEdition
 * @param {boolean} isDraftFromPanel
 * @param {string|number} postingId
 */
export const sendIATrackingInfo = (
  contentType = '',
  itemId = '',
  postingId = null,
  isDraftFromPanel = false,
  isEdition = false,
  operationsTypeIds = [],
  propertyTypeName = '',
  propertySubTypeName = ''
) => {
  const payload = {
    event: 'generate_IA_text',
    content_group: 'Publicador',
    user_type: 'Profesional',
    step: 'Paso 6 - Título y descripción',
    content_type: contentType,
    item_id: itemId,
    operation_type: buildOperationString(operationsTypeIds),
    tipoDePropiedad: propertyTypeName,
    subTipoDePropiedad: propertySubTypeName,
    status: adaptPublicationStatus(isEdition, isDraftFromPanel),
    posting_id: postingId || 'borrador generado'
  };

  sendCustomTrackingInfo(payload);
};
